import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Edit,
  Calendar,
  User,
  Briefcase,
  FileText,
  HardDrive,
  Tag,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { archiveService } from '../services/archive.service';
import { commissionService } from '../services/commission.service';
import Header from '../components/Header';
import ConfirmModal from '../components/ConfirmModal';

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [commissionMembers, setCommissionMembers] = useState([]);

  const fetchArchive = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await archiveService.getArchiveById(id);
      setArchive(data);

      // Charger les membres de la commission si l'archive a une commission
      if (data.commission?.id) {
        try {
          const membersData = await commissionService.getCommissionMembers(data.commission.id);
          const membersArray = membersData?.content || membersData || [];
          setCommissionMembers(membersArray);
        } catch (memberError) {
          console.error('Erreur lors du chargement des membres:', memberError);
          setCommissionMembers([]);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError(error.message || 'Archive introuvable');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchArchive();
  }, [fetchArchive]);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      setDeleteError('');
      await archiveService.deleteArchive(id);
      navigate('/documents');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setDeleteError(error.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Vérifier si l'utilisateur peut gérer l'archive
  const canManageArchive = () => {
    if (!user || !archive) {
      return false;
    }

    // Les administrateurs ont tous les droits
    if (isAdmin()) {
      return true;
    }

    // L'auteur de l'archive peut la gérer
    if (archive.createdBy?.id === user.id) {
      return true;
    }

    // Les membres de la commission peuvent gérer les archives de leur commission
    if (commissionMembers && commissionMembers.length > 0) {
      const isMember = commissionMembers.some((member) => {
        return member.user?.id === user.id ||
               member.userId === user.id ||
               member.id === user.id;
      });
      return isMember;
    }

    return false;
  };

  const canEdit = canManageArchive();
  const canDelete = canManageArchive();

  const getStatusBadgeColor = (status) => {
    const colors = {
      PUBLISHED: 'bg-green-100 text-green-700',
      DRAFT: 'bg-yellow-100 text-yellow-700',
      ARCHIVED: 'bg-gray-100 text-gray-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      PUBLISHED: 'Publié',
      DRAFT: 'Brouillon',
      ARCHIVED: 'Archivé',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div>
        <Header title="Détails de l'archive" />
        <div className="px-8 flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !archive) {
    return (
      <div>
        <Header title="Archive introuvable" />
        <div className="px-8">
          <div className="bg-white rounded-lg shadow-md p-12 text-center max-w-md mx-auto mt-8">
            <AlertCircle className="mx-auto text-red-500 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Archive introuvable</h3>
            <p className="text-gray-500 mb-6">
              {error || "L'archive demandée n'existe pas ou a été supprimée"}
            </p>
            <button
              onClick={() => navigate('/documents')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Retour aux archives</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Détails de l'archive" />

      <div className="px-8">
        <button
          onClick={() => navigate('/documents')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Retour aux archives</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="flex items-start gap-4 mb-6">
                {archive.imageUrl ? (
                  <img
                    src={archive.imageUrl}
                    alt={archive.title}
                    className="w-24 h-24 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                    <FileText className="text-gray-400" size={48} />
                  </div>
                )}
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">{archive.title}</h1>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(
                      archive.status
                    )}`}
                  >
                    {getStatusLabel(archive.status)}
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-3">Description</h2>
                <p className="text-gray-600 leading-relaxed">{archive.description}</p>
              </div>

              {archive.content && (
                <div className="border-t border-gray-200 pt-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Contenu</h2>
                  <div
                    className="prose max-w-none text-gray-600"
                    dangerouslySetInnerHTML={{ __html: archive.content }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Actions</h2>
              <div className="space-y-3">
                {canEdit && (
                  <button
                    onClick={() => navigate(`/documents/edit/${id}`)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-sm"
                  >
                    <Edit size={20} />
                    <span className="font-medium">Modifier</span>
                  </button>
                )}

                {canDelete && (
                  <button
                    onClick={handleDeleteClick}
                    disabled={deleting}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={20} />
                    <span className="font-medium">
                      {deleting ? 'Suppression...' : 'Supprimer'}
                    </span>
                  </button>
                )}

                {archive.fileUrl && (
                  <a
                    href={archive.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={archive.fileName}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm"
                  >
                    <Download size={20} />
                    <span className="font-medium">Télécharger le fichier</span>
                  </a>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Informations</h2>
              <div className="space-y-4">
                {archive.fileName && (
                  <div className="flex items-start gap-3">
                    <FileText className="text-primary-600 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Fichier</p>
                      <p className="font-medium text-gray-800 text-sm break-all">{archive.fileName}</p>
                      {archive.fileSize && (
                        <p className="text-xs text-gray-500 mt-1">
                          {(archive.fileSize / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {archive.commission && (
                  <div className="flex items-start gap-3">
                    <Briefcase className="text-primary-600 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Commission</p>
                      <p className="font-medium text-gray-800">{archive.commission.name}</p>
                    </div>
                  </div>
                )}

                {archive.archiveDate && (
                  <div className="flex items-start gap-3">
                    <Calendar className="text-primary-600 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Date de l'archive</p>
                      <p className="font-medium text-gray-800">
                        {new Date(archive.archiveDate).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {archive.author && (
                  <div className="flex items-start gap-3">
                    <User className="text-primary-600 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Auteur</p>
                      <p className="font-medium text-gray-800">{archive.author}</p>
                    </div>
                  </div>
                )}

                {archive.location && (
                  <div className="flex items-start gap-3">
                    <HardDrive className="text-primary-600 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Lieu</p>
                      <p className="font-medium text-gray-800">{archive.location}</p>
                    </div>
                  </div>
                )}

                {archive.viewsCount !== undefined && (
                  <div className="flex items-start gap-3">
                    <FileText className="text-primary-600 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Vues</p>
                      <p className="font-medium text-gray-800">{archive.viewsCount}</p>
                    </div>
                  </div>
                )}

                {archive.tags && archive.tags.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Tag className="text-primary-600 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {archive.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {archive.createdBy && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-1">Créé par</p>
                    <p className="font-medium text-gray-800">
                      {archive.createdBy.firstName} {archive.createdBy.lastName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Le {new Date(archive.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteError('');
        }}
        onConfirm={handleConfirmDelete}
        title="Supprimer l'archive"
        message={`Êtes-vous sûr de vouloir supprimer définitivement cette archive "${archive?.title}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        loading={deleting}
      />

      {/* Message d'erreur de suppression */}
      {deleteError && (
        <div className="fixed bottom-4 right-4 max-w-md z-50 animate-slide-up">
          <div className="bg-red-50 border border-red-200 rounded-xl shadow-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800 mb-1">Erreur de suppression</p>
              <p className="text-sm text-red-700">{deleteError}</p>
            </div>
            <button
              onClick={() => setDeleteError('')}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <AlertCircle size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDetail;
